import { useState, type ReactNode } from 'react';

import { OverlayDockHost } from '@/shared/ui/primitives';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

export interface PatientOverlayDockTab {
  id: string;
  label: string;
  content: ReactNode;
}

interface PatientOverlayDockProps {
  tabs: PatientOverlayDockTab[];
  persistenceId?: string;
  defaultActiveTabId?: string;
  activeTabId?: string;
  onActiveTabIdChange?: (tabId: string) => void;
}

export function PatientOverlayDock({
  tabs,
  persistenceId = 'patient-overlay-dock',
  defaultActiveTabId,
  activeTabId,
  onActiveTabIdChange,
}: PatientOverlayDockProps) {
  // Store user's tab selection; derive actual active tab from available tabs
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);

  const resolvedActiveTabId = (() => {
    // Controlled mode: use provided activeTabId
    if (activeTabId !== undefined) {
      return activeTabId;
    }
    // User has selected a valid tab
    if (selectedTabId !== null && tabs.some((t) => t.id === selectedTabId)) {
      return selectedTabId;
    }
    // Fall back to default if valid
    if (defaultActiveTabId && tabs.some((t) => t.id === defaultActiveTabId)) {
      return defaultActiveTabId;
    }
    // Last resort: first tab
    return tabs[0]?.id ?? '';
  })();

  const activeTab = tabs.find((t) => t.id === resolvedActiveTabId) ?? null;

  const handleTabChange = (tabId: string) => {
    if (activeTabId === undefined) {
      setSelectedTabId(tabId);
    }
    onActiveTabIdChange?.(tabId);
  };

  return (
    <OverlayDockHost persistenceId={persistenceId}>
      <div className="flex h-full flex-col">
        {tabs.length > 0 ? (
          <>
            <div className="shrink-0 px-2 py-2">
              <Tabs value={resolvedActiveTabId} onValueChange={handleTabChange} className="gap-0">
                <TabsList aria-label="Overlay dock tabs">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="flex-1 overflow-auto">{activeTab?.content}</div>
          </>
        ) : (
          <div className="flex-1" />
        )}
      </div>
    </OverlayDockHost>
  );
}
